import { ObjectCategory, ZIndexes } from "@common/constants";
import { FireCellState, FireConfig } from "@common/definitions/fire";
import { type FireMask, type ObjectsNetData } from "@common/utils/objectsSerializations";
import { Vec, type Vector } from "@common/utils/vector";
import { Container } from "pixi.js";
import { FireEffects } from "../managers/fireEffects";
import { PIXI_SCALE } from "../utils/constants";
import { TrenchioSprite, toPixiCoords } from "../utils/pixi";
import { GameObject } from "./gameObject";

const CELLS = FireConfig.chunkCells;
const CELL_PX = FireConfig.cellSize * PIXI_SCALE;
const FLAME_FRAMES = ["fire_flame_1", "fire_flame_2", "fire_flame_3", "fire_flame_4"];

/** a stable pseudo-random number in [0, 1) for a cell, so every client draws the same fire */
const hash = (n: number): number => {
    let x = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b);
    x ^= x >>> 13; x = Math.imul(x, 0xc2b2ae35); x ^= x >>> 16;
    return (x >>> 0) / 4294967296;
};
const bit = (mask: FireMask, i: number): boolean => (i < 32 ? (mask.lo >>> i) & 1 : (mask.hi >>> (i - 32)) & 1) === 1;

interface Cell {
    state: FireCellState
    ground?: TrenchioSprite
    /** the glowing bed of the fire under the flames: neighbouring beds merge into one burning area */
    bed?: TrenchioSprite
    flames: TrenchioSprite[]
    /** when this cell started burning (client clock), to tell a fire that was put out from one that burnt out */
    burningSince: number
}

/**
 * One chunk (8 x 8 cells) of fire, drawn from the server's masks: scorch marks and poured fuel on the
 * ground, animated flame tongues (additive, so overlapping flames glow together) and kindling flickers
 * above players and bushes (below tree canopies), and one soft firelight glow for the whole chunk.
 */
export class FireChunk extends GameObject.derive(ObjectCategory.Fire) {
    chunkX = 0;
    chunkY = 0;
    private readonly _flames = new Container();
    /** beds first, then the glow, then the flame tongues on top */
    private readonly _beds = new Container();
    private readonly _tongues = new Container();
    private readonly _cells: Cell[] = Array.from({ length: CELLS * CELLS }, () => ({ state: FireCellState.None, flames: [], burningSince: 0 }));
    private readonly _glow: TrenchioSprite;
    private _burning = 0;
    private _kindling = 0;
    private _glowCenter: Vector = Vec(0, 0);

    constructor(id: number, data: ObjectsNetData[ObjectCategory.Fire]) {
        super(id);
        this.container.zIndex = ZIndexes.Decals;
        this._flames.zIndex = ZIndexes.ObstaclesLayer3 + 0.5;
        this.containers.push(this._flames);
        this._glow = new TrenchioSprite("_glow_");
        this._glow.tint = 0xff7a1e;
        this._glow.blendMode = "add";
        this._glow.visible = false;
        this._flames.addChild(this._beds, this._glow, this._tongues);
        this.updateFromData(data, true);
    }

    get burningCells(): number { return this._burning; }
    get worldCenter(): Vector { return this._glowCenter; }

    /** the state of a cell of this chunk (local column and row) */
    cellState(column: number, row: number): FireCellState {
        return this._cells[row * CELLS + column]?.state ?? FireCellState.None;
    }

    override updateFromData(data: ObjectsNetData[ObjectCategory.Fire], isNew = false): void {
        this.chunkX = data.chunkX;
        this.chunkY = data.chunkY;
        const origin = Vec(data.chunkX * CELLS * FireConfig.cellSize, data.chunkY * CELLS * FireConfig.cellSize);
        this.position = origin;
        if (isNew) {
            const pixi = toPixiCoords(origin);
            this.container.position.copyFrom(pixi);
            this._flames.position.copyFrom(pixi);
            this.layer = 0;
            this.updateLayer();
            FireEffects.register(this);
        }

        const now = Date.now();
        let lit = 0, putOut = 0, poured = 0;
        let burning = 0, kindling = 0, sumX = 0, sumY = 0;
        for (let i = 0; i < CELLS * CELLS; i++) {
            const next = bit(data.burning, i) ? FireCellState.Burning
                : bit(data.kindling, i) ? FireCellState.Kindling
                    : bit(data.fuel, i) ? FireCellState.Fuel
                        : bit(data.scorched, i) ? FireCellState.Scorched
                            : FireCellState.None;
            const cell = this._cells[i];
            if (next === FireCellState.Burning) {
                burning++;
                sumX += i % CELLS; sumY += Math.floor(i / CELLS);
            } else if (next === FireCellState.Kindling) {
                kindling++;
            }
            if (next === cell.state) continue;
            const was = cell.state;
            if ((next === FireCellState.Kindling || next === FireCellState.Burning) && was !== FireCellState.Kindling && was !== FireCellState.Burning) lit++;
            if (was === FireCellState.Burning && next !== FireCellState.Burning && now - cell.burningSince < 2500) putOut++;
            if (next === FireCellState.Burning) cell.burningSince = now;
            if (next === FireCellState.Fuel && was !== FireCellState.Kindling && was !== FireCellState.Burning) poured++;
            cell.state = next;
            this._draw(i, cell);
        }
        this._burning = burning;
        this._kindling = kindling;
        if (burning > 0) {
            this._glowCenter = Vec(
                (data.chunkX * CELLS + sumX / burning + 0.5) * FireConfig.cellSize,
                (data.chunkY * CELLS + sumY / burning + 0.5) * FireConfig.cellSize
            );
        }
        this._glow.visible = burning + kindling > 0;
        if (!isNew) FireEffects.chunkChanged(this, lit, putOut);
        if (poured > 0) FireEffects.fuelPoured(this, poured);
    }

    /** (re)build the sprites of one cell for its new state */
    private _draw(i: number, cell: Cell): void {
        const column = i % CELLS, row = Math.floor(i / CELLS);
        const cellId = (this.chunkY * CELLS + row) * 4096 + this.chunkX * CELLS + column;
        const jitter = (k: number): number => (hash(cellId * 7 + k) - 0.5) * CELL_PX * 0.45;
        const center = { x: (column + 0.5) * CELL_PX, y: (row + 0.5) * CELL_PX };

        // the ground: poured fuel, or the scorch left behind (under burning cells too)
        const groundFrame = cell.state === FireCellState.Fuel || cell.state === FireCellState.Kindling
            ? `fuel_puddle_${1 + Math.floor(hash(cellId) * 3)}`
            : cell.state === FireCellState.Burning || cell.state === FireCellState.Scorched
                ? `fire_scorch_${1 + Math.floor(hash(cellId + 11) * 3)}`
                : undefined;
        if (groundFrame) {
            if (!cell.ground) {
                cell.ground = new TrenchioSprite(groundFrame);
                cell.ground.position.set(center.x + jitter(1) * 0.5, center.y + jitter(2) * 0.5);
                cell.ground.rotation = hash(cellId + 3) * Math.PI * 2;
                // big and soft, so neighbouring marks merge into one burnt patch or one oily trail
                const size = (cell.state === FireCellState.Fuel || cell.state === FireCellState.Kindling ? 2.1 : 1.9) * (1 + hash(cellId + 4) * 0.25) * CELL_PX;
                cell.ground.scale.set(size / 96);
                this.container.addChild(cell.ground);
            } else {
                cell.ground.setFrame(groundFrame);
            }
            cell.ground.alpha = cell.state === FireCellState.Burning ? 0.3 : cell.state === FireCellState.Scorched ? 0.55 : 0.62;
        } else if (cell.ground) {
            cell.ground.destroy();
            cell.ground = undefined;
        }

        // the glowing bed under burning (and, faintly, kindling) cells
        const lit = cell.state === FireCellState.Burning || cell.state === FireCellState.Kindling;
        if (lit && !cell.bed) {
            cell.bed = new TrenchioSprite("_glow_");
            cell.bed.tint = hash(cellId + 30) < 0.5 ? 0xff5514 : 0xff7a1c;
            cell.bed.blendMode = "add";
            cell.bed.position.set(center.x + jitter(8) * 0.4, center.y + jitter(9) * 0.4);
            this._beds.addChild(cell.bed);
        } else if (!lit && cell.bed) {
            cell.bed.destroy();
            cell.bed = undefined;
        }

        // flames: two tongues when burning, one small flicker while kindling
        const wanted = cell.state === FireCellState.Burning ? 2 : cell.state === FireCellState.Kindling ? 1 : 0;
        while (cell.flames.length > wanted) cell.flames.pop()?.destroy();
        while (cell.flames.length < wanted) {
            const k = cell.flames.length;
            const flame = new TrenchioSprite(FLAME_FRAMES[Math.floor(hash(cellId + 20 + k) * 4)]);
            flame.anchor.set(0.5, 0.92);
            flame.blendMode = "add";
            // spread the tongues well across (and a little beyond) the cell, so no grid shows
            flame.position.set(center.x + jitter(5 + k * 2) * 1.5, center.y + jitter(6 + k * 2) * 1.5 + CELL_PX * 0.35);
            this._tongues.addChild(flame);
            cell.flames.push(flame);
        }
    }

    override update(): void {
        if (this._burning + this._kindling === 0) return;
        const now = Date.now();
        const lod = FireEffects.lowDetail;
        for (let i = 0; i < this._cells.length; i++) {
            const cell = this._cells[i];
            if (cell.flames.length === 0) continue;
            const phase = hash((this.chunkY * 4096 + this.chunkX) * 131 + i);
            const kindling = cell.state === FireCellState.Kindling;
            if (cell.bed) {
                const breathe = 0.85 + 0.15 * Math.sin(now * 0.006 + phase * 30);
                cell.bed.scale.set((kindling ? 0.9 : 1.75) * CELL_PX / 640 * 2.2 * breathe);
                cell.bed.alpha = (kindling ? 0.2 : 0.42) * breathe;
            }
            for (let k = 0; k < cell.flames.length; k++) {
                const flame = cell.flames[k];
                if (k === 1 && lod) { flame.visible = false; continue; }
                flame.visible = true;
                // cycle through the four flame shapes at about 11 frames a second, each cell in its own rhythm
                const frame = Math.floor(now / 90 + phase * 17 + k * 2) % FLAME_FRAMES.length;
                flame.setFrame(FLAME_FRAMES[frame]);
                const flicker = 0.9 + 0.14 * Math.sin(now * 0.011 + phase * 40 + k * 1.7) + 0.06 * Math.sin(now * 0.031 + phase * 13);
                // big, varied tongues that rise and fall
                const base = kindling ? 0.55 + 0.12 * Math.sin(now * 0.05 + phase * 9) : (k === 0 ? 1.65 : 1.2) * (0.82 + hash((phase * 1e6) | 0) * 0.45);
                const rise = 1 + 0.16 * Math.sin(now * 0.017 + phase * 5 + k);
                flame.scale.set(base * flicker * CELL_PX / 72, base * flicker * rise * CELL_PX / 72);
                flame.alpha = kindling ? 0.66 : 0.72 + 0.14 * Math.sin(now * 0.023 + phase * 21);
                flame.rotation = FireEffects.lean * (0.6 + phase * 0.5);
            }
        }
        // one firelight glow for the chunk, breathing with the flames
        const count = this._burning + this._kindling * 0.4;
        const pulse = 0.9 + 0.1 * Math.sin(now * 0.009 + this.id);
        const glowPx = (2.2 + Math.sqrt(count) * 1.35) * CELL_PX;
        const center = toPixiCoords(this._burning > 0 ? this._glowCenter : Vec(this.position.x + CELLS * FireConfig.cellSize / 2, this.position.y + CELLS * FireConfig.cellSize / 2));
        this._glow.position.set(center.x - this._flames.position.x, center.y - this._flames.position.y);
        this._glow.scale.set(glowPx / 640 * pulse);
        this._glow.alpha = Math.min(0.55, 0.18 + count * 0.02) * pulse;
        FireEffects.emit(this, now);
    }

    override updateInterpolation(): void { /* fire doesn't move */ }

    override updateDebugGraphics(): void { /* no debug outline */ }

    override destroy(): void {
        FireEffects.unregister(this);
        this._flames.destroy({ children: true });
        super.destroy();
    }
}
